import React, { useState, useEffect, useCallback } from 'react';
import { Calendar, momentLocalizer } from 'react-big-calendar';
import { DndProvider } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import moment from 'moment';
import axios from 'axios';
import { Button } from './ui/button';
import { Card } from './ui/card';
import { 
  Calendar as CalendarIcon, Users, Truck, 
  RefreshCw, Filter, Eye, EyeOff
} from 'lucide-react';
import { toast } from 'sonner';
import 'react-big-calendar/lib/css/react-big-calendar.css';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const localizer = momentLocalizer(moment);

const DriversCalendar = ({ getAuthHeaders }) => {
  const [jobs, setJobs] = useState([]);
  const [drivers, setDrivers] = useState([]);
  const [selectedDrivers, setSelectedDrivers] = useState(new Set(['unassigned'])); // Show unassigned by default
  const [isLoading, setIsLoading] = useState(true);
  const [showAllDrivers, setShowAllDrivers] = useState(true);
  const [lastRefresh, setLastRefresh] = useState(Date.now());

  // Auto-refresh every 30 seconds
  useEffect(() => {
    const interval = setInterval(() => {
      fetchJobs();
      setLastRefresh(Date.now());
    }, 30000);

    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    fetchJobs();
    fetchDrivers();
  }, []);

  const fetchJobs = async () => {
    try {
      const response = await axios.get(`${API}/jobs`, getAuthHeaders());
      setJobs(response.data);
    } catch (error) {
      console.error('Failed to fetch jobs:', error);
      toast.error('Failed to load jobs');
    }
  };

  const fetchDrivers = async () => {
    try {
      const response = await axios.get(`${API}/drivers`, getAuthHeaders());
      setDrivers(response.data.filter(d => d.is_active));
    } catch (error) {
      console.error('Failed to fetch drivers:', error);
      // Don't show error toast since driver module might not be enabled
    } finally {
      setIsLoading(false);
    }
  };

  const handleManualRefresh = () => {
    fetchJobs();
    fetchDrivers();
    toast.success('Data refreshed');
  };

  // Convert jobs to calendar events
  const getEventsForDriver = (driverId) => {
    const driverJobs = driverId === 'unassigned' 
      ? jobs.filter(job => !job.assigned_driver_id)
      : jobs.filter(job => job.assigned_driver_id === driverId);

    return driverJobs
      .filter(job => job.scheduled_start)
      .map(job => ({
        id: job.id,
        title: `${job.job_number} - ${job.customer_name || 'Unknown'}`,
        start: new Date(job.scheduled_start),
        end: job.scheduled_end ? new Date(job.scheduled_end) : 
             new Date(new Date(job.scheduled_start).getTime() + 2 * 60 * 60 * 1000), // Default 2 hours
        resource: {
          job,
          driverId
        }
      }));
  };

  const handleEventDrop = async ({ event, start, end, resourceId: newDriverId }) => {
    try {
      const jobId = event.resource.job.id;
      const oldDriverId = event.resource.driverId;

      // Update job assignment
      await axios.patch(
        `${API}/jobs/${jobId}`,
        { 
          assigned_driver_id: newDriverId === 'unassigned' ? null : newDriverId,
          scheduled_start: start.toISOString(),
          scheduled_end: end.toISOString()
        },
        getAuthHeaders()
      );

      const driverName = newDriverId === 'unassigned' ? 'Unassigned' :
                        drivers.find(d => d.id === newDriverId)?.first_name + ' ' +
                        drivers.find(d => d.id === newDriverId)?.last_name || 'Unknown';

      toast.success(`Job assigned to ${driverName}`);
      fetchJobs(); // Refresh data
    } catch (error) {
      console.error('Failed to update job assignment:', error);
      toast.error('Failed to update job assignment');
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'scheduled': return '#3B82F6'; // blue
      case 'in_progress': return '#F59E0B'; // yellow
      case 'completed': return '#10B981'; // green
      case 'paid': return '#8B5CF6'; // purple
      case 'cancelled': return '#EF4444'; // red
      default: return '#6B7280'; // gray
    }
  };

  const eventStyleGetter = (event) => {
    return {
      style: {
        backgroundColor: getStatusColor(event.resource.job.status),
        borderColor: getStatusColor(event.resource.job.status),
        color: 'white',
        border: 'none',
        borderRadius: '4px',
        fontSize: '12px',
        padding: '2px 4px'
      }
    };
  };

  const toggleDriverVisibility = (driverId) => {
    const newSelected = new Set(selectedDrivers);
    if (newSelected.has(driverId)) {
      newSelected.delete(driverId);
    } else {
      newSelected.add(driverId);
    }
    setSelectedDrivers(newSelected);
  };

  const toggleAllDrivers = () => {
    if (showAllDrivers) {
      setSelectedDrivers(new Set(['unassigned']));
    } else {
      setSelectedDrivers(new Set(['unassigned', ...drivers.map(d => d.id)]));
    }
    setShowAllDrivers(!showAllDrivers);
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  const visibleDrivers = drivers.filter(d => selectedDrivers.has(d.id));
  const allEvents = [
    ...(selectedDrivers.has('unassigned') ? getEventsForDriver('unassigned') : []),
    ...visibleDrivers.flatMap(driver => getEventsForDriver(driver.id))
  ];

  return (
    <DndProvider backend={HTML5Backend}>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-3xl font-bold">Drivers Calendar</h1>
            <p className="text-gray-500 mt-1">Drag and drop jobs to assign drivers</p>
          </div>
          <div className="flex gap-2">
            <Button 
              variant="outline" 
              onClick={handleManualRefresh}
              className="gap-2"
            >
              <RefreshCw className="w-4 h-4" />
              Refresh
            </Button>
            <Button 
              variant="outline" 
              onClick={toggleAllDrivers}
              className="gap-2"
            >
              {showAllDrivers ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              {showAllDrivers ? 'Hide All' : 'Show All'}
            </Button>
          </div>
        </div>

        {/* Stats & Controls */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Card className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Total Jobs</p>
                <p className="text-2xl font-bold">{jobs.length}</p>
              </div>
              <CalendarIcon className="w-8 h-8 text-blue-500" />
            </div>
          </Card>
          
          <Card className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Unassigned</p>
                <p className="text-2xl font-bold">{jobs.filter(j => !j.assigned_driver_id).length}</p>
              </div>
              <Filter className="w-8 h-8 text-orange-500" />
            </div>
          </Card>
          
          <Card className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Active Drivers</p>
                <p className="text-2xl font-bold">{drivers.length}</p>
              </div>
              <Users className="w-8 h-8 text-green-500" />
            </div>
          </Card>
          
          <Card className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Assigned Jobs</p>
                <p className="text-2xl font-bold">{jobs.filter(j => j.assigned_driver_id).length}</p>
              </div>
              <Truck className="w-8 h-8 text-purple-500" />
            </div>
          </Card>
        </div>

        {/* Driver Toggles */}
        <Card className="p-4">
          <h3 className="font-semibold mb-3">Driver Visibility</h3>
          <div className="flex flex-wrap gap-2">
            <Button
              variant={selectedDrivers.has('unassigned') ? 'default' : 'outline'}
              size="sm"
              onClick={() => toggleDriverVisibility('unassigned')}
              className="gap-2"
            >
              <Filter className="w-3 h-3" />
              Unassigned ({jobs.filter(j => !j.assigned_driver_id).length})
            </Button>
            
            {drivers.map(driver => (
              <Button
                key={driver.id}
                variant={selectedDrivers.has(driver.id) ? 'default' : 'outline'}
                size="sm"
                onClick={() => toggleDriverVisibility(driver.id)}
                className="gap-2"
              >
                <Truck className="w-3 h-3" />
                {driver.first_name} {driver.last_name} 
                ({jobs.filter(j => j.assigned_driver_id === driver.id).length})
              </Button>
            ))}
          </div>
        </Card>

        {/* Calendar */}
        <Card className="p-6">
          <div style={{ height: '600px' }}>
            <Calendar
              localizer={localizer}
              events={allEvents}
              startAccessor="start"
              endAccessor="end"
              views={['month', 'week', 'day']}
              defaultView="week"
              step={30}
              timeslots={2}
              eventPropGetter={eventStyleGetter}
              onSelectEvent={(event) => {
                // Could open job details modal here
                const job = event.resource.job;
                toast.info(`Job: ${job.job_number} - ${job.customer_name || 'Unknown'}`);
              }}
              // Note: Drag and drop would require additional setup with react-big-calendar-drag-drop
              // For now, we'll handle reassignment through other means
              components={{
                event: ({ event }) => (
                  <div className="text-xs">
                    <div className="font-semibold truncate">
                      {event.resource.job.job_number}
                    </div>
                    <div className="truncate">
                      {event.resource.job.customer_name || 'Unknown'}
                    </div>
                    <div className="opacity-75">
                      {event.resource.job.status.toUpperCase()}
                    </div>
                  </div>
                )
              }}
              formats={{
                timeGutterFormat: 'HH:mm',
                eventTimeRangeFormat: ({ start, end }) => 
                  `${moment(start).format('HH:mm')} - ${moment(end).format('HH:mm')}`
              }}
            />
          </div>
        </Card>

        {/* Legend */}
        <Card className="p-4">
          <h3 className="font-semibold mb-3">Status Legend</h3>
          <div className="flex flex-wrap gap-4">
            {[
              { status: 'scheduled', label: 'Scheduled' },
              { status: 'in_progress', label: 'In Progress' },
              { status: 'completed', label: 'Completed' },
              { status: 'paid', label: 'Paid' },
              { status: 'cancelled', label: 'Cancelled' }
            ].map(({ status, label }) => (
              <div key={status} className="flex items-center gap-2">
                <div 
                  className="w-4 h-4 rounded"
                  style={{ backgroundColor: getStatusColor(status) }}
                ></div>
                <span className="text-sm">{label}</span>
              </div>
            ))}
          </div>
        </Card>

        {/* Auto-refresh indicator */}
        <div className="text-center text-sm text-gray-500">
          Auto-refreshes every 30 seconds • Last refresh: {moment(lastRefresh).format('HH:mm:ss')}
        </div>
      </div>
    </DndProvider>
  );
};

export default DriversCalendar;